/**
 * 딜등록 「만료 안내 메일 문구」 고치기 창 (v2.147.0 · 2026-09-09)
 *
 * 사용자 요청: 「딜등록 만료 안내 멘트를 수정할 수 있는 버튼을 딜등록 메뉴 안에 만들어줘.」
 *
 * 메일 본문은 세 덩이다 —
 *   ① 앞 문구(인사·안내)  ② **딜 정보 줄(자동)**  ③ 뒤 문구(요청·맺음)
 * 가운데는 자료라 사람이 못 고친다(접수번호·만료일 등은 딜마다 서버가 채운다).
 * 고치는 것은 앞뒤 문구뿐이고, 저장은 app_config 두 줄이다.
 *
 * ⭐미리보기는 **서버**가 만든다(`/expiry-mail/preview`) — 실제 메일을 만드는 함수와 같은 것을 쓴다.
 *   화면에서 글을 다시 조립하면 「미리보기와 실제 메일이 다른」 자리가 생긴다.
 * ⛔배경(바깥) 클릭으로 닫지 않는다 — 적던 글이 사라진다(check:modal).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/deal-registration-api';
import { LoadingLines } from './ui/Skeleton';

interface Props {
  onClose: () => void;
}

export function ExpiryMailTemplateModal({ onClose }: Props) {
  const qc = useQueryClient();
  const tplQuery = useQuery({ queryKey: ['deals', 'expiryTemplate'], queryFn: api.getExpiryTemplate });
  const tpl = tplQuery.data;

  const [intro, setIntro] = useState('');
  const [closing, setClosing] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** 불러온 뒤 한 번만 칸을 채운다 — 이후에는 사람이 고친 값을 덮지 않는다 */
  const loadedRef = useRef(false);
  /**
   * 「열었을 때」 값 — 닫기 전에 「바뀌었나」를 잰다 (ExpenseDocModal 과 같은 방식).
   * **두 칸을 따로 담는다** (v2.153.0).
   *
   * ⚠전에는 두 칸을 구분자 하나로 이어 붙인 글자를 담았는데, 그 구분자를 **진짜 0번 바이트**로
   *   적어 놓아 git 이 이 파일을 「이진 파일」로 취급했다 — diff·검색·머지가 통째로 막혔다.
   *   칸을 따로 비교하면 구분자 자체가 필요 없다.
   */
  const baseRef = useRef<{ intro: string; closing: string }>({ intro: '', closing: '' });

  useEffect(() => {
    if (!tpl || loadedRef.current) return;
    loadedRef.current = true;
    setIntro(tpl.intro);
    setClosing(tpl.closing);
    baseRef.current = { intro: tpl.intro, closing: tpl.closing };
  }, [tpl]);

  const canEdit = tpl?.canEdit ?? false;
  const dirty = loadedRef.current && (intro !== baseRef.current.intro || closing !== baseRef.current.closing);

  /* 미리보기 — 글을 멈추고 0.6초 뒤에 서버에 물어본다(글자마다 부르지 않게) */
  const [preview, setPreview] = useState<string>('');
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    if (!loadedRef.current) return;
    let alive = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      api.previewExpiryTemplate({ intro, closing })
        .then((r) => { if (alive) setPreview(r.body); })
        .catch((e) => { if (alive) setPreview(`(미리보기를 만들지 못했습니다 — ${(e as Error).message})`); })
        .finally(() => { if (alive) setPreviewing(false); });
    }, 600);
    return () => { alive = false; clearTimeout(t); };
  }, [intro, closing]);

  const save = useMutation({
    /**
     * ⭐기본 문구와 **글자까지 똑같으면 빈 칸으로 저장**한다 = 저장해 둔 줄을 지워
     *   「기본 문구 사용 중」으로 돌아간다 (v2.153.0).
     *   전에는 [↺ 기본 문구로 되돌리기] 를 누르고 저장하면 같은 글자가 「고친 문구」로 박제되어,
     *   나중에 코드의 기본 문구를 손봐도 그 딜 안내문은 옛 글자에 묶여 있었다.
     */
    mutationFn: () => {
      const backToDefault = !!tpl && intro === tpl.defaults.intro && closing === tpl.defaults.closing;
      return api.saveExpiryTemplate(backToDefault ? { intro: '', closing: '' } : { intro, closing });
    },
    onSuccess: (r) => {
      setError(null);
      baseRef.current = { intro: r.intro, closing: r.closing };
      setIntro(r.intro);
      setClosing(r.closing);
      void qc.invalidateQueries({ queryKey: ['deals'] });
      alert('만료 안내 문구를 저장했습니다.\n이제 [✉ 만료 안내 메일] 을 누르면 이 문구로 채워집니다.');
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const close = (): void => {
    if (dirty && !confirm('고친 문구가 있습니다.\n닫으면 저장하지 않은 내용이 사라집니다.\n\n그래도 닫을까요?')) return;
    onClose();
  };

  const resetToDefault = (): void => {
    if (!tpl) return;
    if (!confirm('앞뒤 문구를 처음 기본 문구로 되돌립니다.\n(저장을 눌러야 실제로 바뀝니다)')) return;
    setIntro(tpl.defaults.intro);
    setClosing(tpl.defaults.closing);
  };

  const varHelp = useMemo(() => tpl?.vars ?? [], [tpl]);
  const taCls = 'ui-input ui-textarea w-full px-2.5 py-2 leading-6 font-mono';

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-[980px] max-w-[97vw] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h3 className="ui-subsection-title">✉ 만료 안내 메일 문구</h3>
            {tpl && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${
                tpl.isDefault ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                {tpl.isDefault ? '기본 문구 사용 중' : `고친 문구 사용 중 (${tpl.savedAt} 저장)`}
              </span>
            )}
            {!canEdit && tpl && (
              <span className="text-xs text-amber-700">보기만 할 수 있습니다 — 고치기는 경영지원·관리자</span>
            )}
          </div>
          <button type="button" onClick={close} aria-label="닫기"
            className="ui-close">×</button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-3">
          <p className="text-xs text-slate-500 leading-5">
            딜등록 목록의 ✉ 또는 상세의 [✉ 만료 안내 메일] 을 누르면 제안사에 보낼 메일 창이 이 문구로 채워집니다.
            <b className="text-slate-700"> 가운데 「딜 정보 줄」(접수번호 · Project ID · 엔드유저 · 프로젝트 · 등록일 · 만료일)은 딜마다 자동으로 채워지고 고칠 수 없습니다.</b>
            {' '}메일 창에서 보내기 전에 한 번 더 고칠 수 있습니다.
          </p>

          {error && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2 whitespace-pre-line">{error}</div>
          )}

          {tplQuery.isLoading ? (
            <div className="text-sm text-slate-400 py-8 text-center"><LoadingLines /></div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1">① 앞 문구 <span className="font-normal text-slate-400">(인사 · 안내)</span></span>
                  <textarea value={intro} rows={5} maxLength={4000} disabled={!canEdit}
                    onChange={(e) => setIntro(e.target.value)} className={taCls} />
                </label>

                <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-2">
                  <div className="text-xs font-semibold text-slate-500 mb-1">② 딜 정보 줄 — 자동 (고칠 수 없음)</div>
                  <pre className="text-xs text-slate-500 leading-5 whitespace-pre-wrap">{
                    '· 접수번호 : …\n· Project ID : …\n· 엔드유저 : …\n· 프로젝트 : …\n· 등록일 : …\n· 만료일 : … (연장 요청 마감 …)'
                  }</pre>
                  <div className="text-xs text-slate-500 mt-1">비어 있는 칸은 그 줄이 통째로 빠집니다.</div>
                </div>

                <label className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1">③ 뒤 문구 <span className="font-normal text-slate-400">(요청 · 맺음)</span></span>
                  <textarea value={closing} rows={7} maxLength={4000} disabled={!canEdit}
                    onChange={(e) => setClosing(e.target.value)} className={taCls} />
                </label>

                <div className="rounded border border-slate-200 bg-white px-3 py-2">
                  <div className="text-xs font-semibold text-slate-600 mb-1">문구 안에 쓸 수 있는 자리 <span className="font-normal text-slate-400">— 중괄호까지 그대로 적으면 값으로 바뀝니다</span></div>
                  <div className="flex flex-wrap gap-1.5">
                    {varHelp.map((v) => (
                      <span key={v.name} title={v.what}
                        className="text-xs px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 font-mono">
                        {`{${v.name}}`}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col min-h-0">
                <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-2">
                  미리보기 <span className="font-normal text-slate-400">(예시 딜로 만든 실제 본문)</span>
                  {previewing && <span className="text-xs text-slate-500">만드는 중…</span>}
                </div>
                <pre className="flex-1 min-h-[380px] overflow-auto rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-6 whitespace-pre-wrap text-slate-700">
                  {preview || ' '}
                </pre>
                <div className="text-xs text-slate-500 mt-1">
                  받는사람 · 제목 · 꼬리말(서명)은 메일 창이 채웁니다.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b flex-wrap">
          <button type="button" onClick={resetToDefault} disabled={!canEdit || !tpl}
            className="btn btn-secondary">
            ↺ 기본 문구로 되돌리기
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={close} disabled={save.isPending}
              className="btn btn-secondary">
              {canEdit ? '취소' : '닫기'}
            </button>
            {canEdit && (
              <button type="button" onClick={() => save.mutate()} disabled={save.isPending || !dirty}
                className="btn btn-primary">
                {save.isPending ? '저장 중…' : '저장'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
